import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMutation as useApolloMutation } from "@apollo/client/react";
import { toast } from "sonner";
import {
  CreateDealDocument,
  UpdateDealDocument,
  DeleteDealDocument,
  type CreateDealMutation,
  type UpdateDealMutation,
  type DeleteDealMutation,
  type Deals,
  type DealsUpdateInput,
  type DealsInsertInput,
} from "@/src/gql/graphql";
import { DealsUpdateInputSchema, DealsInsertInputSchema } from "@/src/gql/validation";

import { stringifyDecimalFields } from "./decimal-fields";
import { normalizeGraphqlDatetimeInput } from "@/lib/graphql/serialize-input";

/**
 * Hook for creating a new deal
 */
export function useCreateDeal() {
  const queryClient = useQueryClient();
  const [createDeal] = useApolloMutation<CreateDealMutation>(CreateDealDocument);

  return useMutation({
    mutationFn: async (input: DealsInsertInput) => {
      // Validate data before database update
      const validatedData = DealsInsertInputSchema.parse(input);

      const finalInput = normalizeGraphqlDatetimeInput(
        stringifyDecimalFields(validatedData)
      ) as DealsInsertInput;

      const { data } = await createDeal({
        variables: { input: finalInput },
      });

      if (!data?.insertIntodealsCollection?.records?.[0]) {
        throw new Error("Failed to create deal");
      }

      return data.insertIntodealsCollection.records[0] as Deals;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success("Deal created successfully");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to create deal");
    },
  });
}

/**
 * Hook for updating an existing deal
 */
export function useUpdateDeal() {
  const queryClient = useQueryClient();
  const [updateDeal] = useApolloMutation<UpdateDealMutation>(UpdateDealDocument);

  return useMutation({
    mutationFn: async (input: DealsUpdateInput & { id: string }) => {
      const { id, ...updateInput } = input;

      // Validate data before database update
      const validatedData = DealsUpdateInputSchema.parse(updateInput);

      const finalInput = normalizeGraphqlDatetimeInput(
        stringifyDecimalFields(validatedData)
      ) as DealsUpdateInput;

      const { data } = await updateDeal({
        variables: { id, input: finalInput },
      });

      if (!data?.updatedealsCollection?.records?.[0]) {
        throw new Error("Failed to update deal");
      }

      return data.updatedealsCollection.records[0] as Deals;
    },
    onMutate: async (input) => {
      const { id } = input;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["deals"] });
      await queryClient.cancelQueries({ queryKey: ["deal", id] });
      await queryClient.cancelQueries({ queryKey: ["deals-paginated"] });

      // Snapshot previous values
      const previousDeals = queryClient.getQueryData<Deals[]>(["deals"]);
      const previousDeal = queryClient.getQueryData<Deals>(["deal", id]);
      const previousPaginated = queryClient.getQueryData<{ data: Deals[] }>(["deals-paginated"]);

      // Optimistically update single deal cache
      queryClient.setQueryData(["deal", id], (old: Deals | undefined) => {
        if (!old) return old;
        const { id: _id, ...updates } = input;
        return { ...old, ...updates } as Deals;
      });

      // Optimistically update deals list
      queryClient.setQueryData(["deals"], (old: Deals[] | undefined) => {
        if (!old) return old;
        const { id: _id, ...updates } = input;
        return old.map((deal) => (deal.id === id ? { ...deal, ...updates } : deal)) as Deals[];
      });

      // Optimistically update paginated deals
      queryClient.setQueryData(["deals-paginated"], (old: { data: Deals[] } | undefined) => {
        if (!old) return old;
        const { id: _id, ...updates } = input;
        return {
          ...old,
          data: old.data?.map((deal: Deals) => (deal.id === id ? { ...deal, ...updates } : deal)),
        };
      });

      return { previousDeals, previousDeal, previousPaginated };
    },
    onSuccess: (data: Deals) => {
      // Update caches with server response
      queryClient.setQueryData(["deal", data.id], data);

      queryClient.setQueryData(["deals"], (old: Deals[] | undefined) => {
        if (!old) return old;
        return old.map((deal) => (deal.id === data.id ? data : deal));
      });

      queryClient.setQueryData(["deals-paginated"], (old: { data: Deals[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((deal: Deals) => (deal.id === data.id ? data : deal)),
        };
      });

      toast.success("Deal updated successfully");
    },
    onError: (
      error,
      _variables,
      context:
        | { previousDeals?: Deals[]; previousDeal?: Deals; previousPaginated?: { data: Deals[] } }
        | undefined
    ) => {
      // Rollback to previous values
      if (context?.previousDeals) {
        queryClient.setQueryData(["deals"], context.previousDeals);
      }
      if (context?.previousDeal) {
        queryClient.setQueryData(["deal", _variables.id], context.previousDeal);
      }
      if (context?.previousPaginated) {
        queryClient.setQueryData(["deals-paginated"], context.previousPaginated);
      }

      toast.error(error instanceof Error ? error.message : "Failed to update deal");
    },
  });
}

/**
 * Hook for deleting a deal
 */
export function useDeleteDeal() {
  const queryClient = useQueryClient();
  const [deleteDeal] = useApolloMutation<DeleteDealMutation>(DeleteDealDocument);

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteDeal({
        variables: { id },
      });

      if (!data?.deleteFromdealsCollection?.records?.[0]) {
        throw new Error("Failed to delete deal");
      }

      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success("Deal deleted successfully");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to delete deal");
    },
  });
}
